import { useMap } from "@vis.gl/react-google-maps";
import { Button, Checkbox, Modal, Switch } from "antd";
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InputSearch from "../../components/Basic/InputSearch";
import Icon, { ICON_NAME } from "../../components/Icon";
import Header from "../../components/Popup/_components/Header";
import { MAP_ELEMENT } from "../../contexts/WorldMapContext/constants";
import { RouteService } from "../../util/services/route";
import { selectFactory } from "../../util/store/factory/constants";
import { selectLocation } from "../../util/store/location/constants";
import { selectSite } from "../../util/store/site/constants";
import { useSelector } from "../../util/store/store";
import { propsModal } from "../../util/varibles/constants";
import { showErrorResponse } from "../../util/varibles/global";
import { notify, NotifyCode } from "../../util/varibles/message";
import { openAutoRoute } from "../RoutePage/_component/PopupAutoRoute";
import { getRouteUpgrade, setRouteUpgrade, useRouteUpgrade } from "./RouteUpgradeContext";
import styles from "./style.module.scss";

interface IOpts {
    opts: { id?: string, name?: string, position?: { latitude: number, longitude: number } }[],
    type: MAP_ELEMENT,
    loading: boolean,
    selectedIds: Set<string>
}

const Opts: React.FC<IOpts> = ({ opts, loading, type, selectedIds }) => {
    const set = setRouteUpgrade()

    const handleChange = useCallback((checked: boolean, item: IOpts["opts"][0]) => {
        set(({ locations: prev }) => {
            if (checked) {
                const value = {
                    id: item.id,
                    name: item.name,
                    type,
                    position: item.position,
                } as typeof prev[0]
                return { locations: [...prev, value] }
            } else
                return { locations: prev.filter(loc => loc.id !== item.id) }
        })
    }, [set, type])

    return <div className={styles.opts}>
        {opts.map(item => {
            return <Checkbox
                key={item.id}
                className='py-1'
                checked={selectedIds.has(item.id || "")}
                onChange={({ target }) => handleChange(target.checked, item)}
            >
                {item.name}
            </Checkbox>
        })}
    </div>
}

const HeaderLocationType: React.FC<{
    children: ReactNode | string,
    search: string,
    onSearch(value: string): void
}> = ({ children, search, onSearch }) => {
    return <div className={styles.headerLocationType}>
        <span>{children}</span>
        <InputSearch value={search} onChange={onSearch} />
    </div>
}

const Sites: React.FC<{ selectedIds: Set<string> }> = ({ selectedIds }) => {
    const opts = useSelector(selectSite.data);
    const loading = useSelector(selectSite.loading);
    const [search, setSearch] = useState('')

    const filter = useMemo(() => opts.filter(item => item.name.toLowerCase().includes(search.toLowerCase())), [opts, search]);

    return <div className='w-fiull'>
        <HeaderLocationType search={search} onSearch={setSearch}>
            Sites ({opts.length})
        </HeaderLocationType>
        <Opts selectedIds={selectedIds} opts={filter} type={MAP_ELEMENT.SITE} loading={loading} />
    </div>
}

const Factories: React.FC<{ selectedIds: Set<string> }> = ({ selectedIds }) => {
    const opts = useSelector(selectFactory.factoriesAdmin);
    const loading = useSelector(selectFactory.loading);
    const [search, setSearch] = useState('')

    const filter = useMemo(() => opts.filter(item => item.name.toLowerCase().includes(search.toLowerCase())), [opts, search]);

    return <div className='w-fiull'>
        <HeaderLocationType search={search} onSearch={setSearch}>
            Factories ({opts.length})
        </HeaderLocationType>
        <Opts selectedIds={selectedIds} opts={filter} type={MAP_ELEMENT.FACTORY} loading={loading} />
    </div>
}

const Pois: React.FC<{ selectedIds: Set<string> }> = ({ selectedIds }) => {
    const opts = useSelector(selectLocation.data);
    const loading = useSelector(selectLocation.loading);
    const [search, setSearch] = useState('')

    const filter = useMemo(() => opts.filter(item => item.name.toLowerCase().includes(search.toLowerCase())), [opts, search]);

    return <div className='w-fiull'>
        <HeaderLocationType search={search} onSearch={setSearch}>
            Pois ({opts.length})
        </HeaderLocationType>
        <Opts selectedIds={selectedIds} opts={filter} type={MAP_ELEMENT.POI} loading={loading} />
    </div>
}

const AddLocation: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const [locations] = useRouteUpgrade(state => state.locations);
    const selectedIds = useMemo(() => new Set(locations.map(item => item.id)), [locations]);

    const onClose = () => {
        setVisible(false);
    }

    return <>
        <Button className="bt-primary" data-icon='true' onClick={() => setVisible(true)}>
            <Icon className='ml-2' icon={ICON_NAME.ADD_2} size='small' />
            <span className='pr-3'>Add</span>
        </Button>
        <Modal
            {...propsModal}
            className='max-h-full'
            width="100%"
            open={visible}
            title={<Header title='Add location' onClose={onClose} />}
            onCancel={onClose}
        >
            <div className={styles.tabs}>
                <div className='grid grid-cols-2 gap-[10px] w-full'>
                    <Factories selectedIds={selectedIds} />
                    <Pois selectedIds={selectedIds} />
                </div>
                <Sites selectedIds={selectedIds} />
            </div>
        </Modal>
    </>
}

const Locations = () => {
    const [locations, set] = useRouteUpgrade(state => state.locations);
    const map = useMap();

    const onClick = (item: typeof locations[0]) => {
        map?.setCenter({ lat: item.position?.latitude || 0, lng: item.position?.longitude || 0 })
    }

    const onDelete = (e: React.MouseEvent, i: number) => {
        e.stopPropagation();
        e.preventDefault();
        set(({ locations }) => {
            return { locations: locations.filter((_, index) => index !== i) };
        })
    }

    return <div className='pt-3 flex flex-col gap-1'>
        {locations.map((item, i) => <div className={styles.location} key={item.id} onClick={() => onClick(item)}>
            <span>{i + 1}</span>
            <span>{item.name}</span>
            <Button className='bt-ghost absolute right-[8px] top-[4px]' data-icon="true" data-danger="true"
                onClick={e => onDelete(e, i)}>
                <Icon icon={ICON_NAME.DELETE} />
            </Button>
        </div>)}
    </div>
}

const SetCenter = () => {
    const map = useMap();
    const get = getRouteUpgrade();
    const onClick = () => {
        const { locations } = get();
        if (locations.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            locations.forEach(item => {
                if (item.position)
                    bounds.extend(new google.maps.LatLng(item.position.latitude, item.position.longitude));
            })
            map?.fitBounds(bounds);
        }
    }

    return <Button className="bt-primary" data-icon='true' onClick={onClick}>
        <span className='px-3'>Center</span>
    </Button>
}

const AutoRoute = () => {
    const [requestId, set] = useRouteUpgrade(state => state.requestId)
    const get = getRouteUpgrade();
    const abort = useRef(new AbortController());
    const timeout = useRef(setTimeout(() => {
    }, 1));

    useEffect(() => {
        return () => {
            set({ requestId: '-', loading: false })
            abort.current.abort()
        };
    }, [set])

    useEffect(() => {
        if (requestId === '-' && timeout.current)
            clearTimeout(timeout.current);
    }, [requestId])

    const onClick = () => {
        const { locations } = get();
        if (locations.length > 0) {
            openAutoRoute({
                onOk: (params: any) => {
                    const { optimize } = params;
                    const body = {
                        nodes: locations.map(item => ({
                            id: item.id,
                            name: item.name,
                            position: `${item.position?.latitude} ${item.position?.longitude}`
                        })),
                        optimize,
                        request_id: ['auto-route', Date.now()].join('-'),
                    }
                    set({ requestId: body.request_id, loading: true });
                    if (timeout.current)
                        clearTimeout(timeout.current);
                    abort.current = new AbortController();
                    timeout.current = setTimeout(() => {
                        set({ requestId: '-', loading: false });
                        notify.error(NotifyCode.E46)();
                    }, 60000)
                    RouteService.callAutoRoute(body, abort.current)
                        .then(() => {
                        })
                        .catch(error => {
                            set({ requestId: '-', loading: false })
                            showErrorResponse(notify[NotifyCode.E46](), error)
                        })
                }
            })
        }
    }

    return <Button className='bt-primary' data-icon="true" loading={requestId !== '-'} onClick={onClick}>
        {requestId === '-' && <span className='px-2'>Get auto route</span>}
    </Button>
}

const OnOffPointer = () => {
    const [onlySeaway, set] = useRouteUpgrade(state => state.onlySeaway);

    return <Switch
        checked={!onlySeaway}
        checkedChildren="Hide points on Seaway"
        unCheckedChildren="Show points on Seaway"
        onChange={checked => set({ onlySeaway: !checked })}
    />
}

interface IProps {
}

const Control: React.FC<IProps> = () => {

    return <div className={styles.control}>
        <div className={styles.headerControl}>
            <div className='flex items-center justify-content-between mb-2'>
                <div className='font-[600] mb-2 text-center'>
                    Auto route through multiple points
                </div>
                <AddLocation />
            </div>
            <div className='flex items-center justify-between gap-2 w-full'>
                <SetCenter />
                <OnOffPointer />
                <AutoRoute />
            </div>
        </div>
        <Locations />
    </div>;
};

export default Control;
